/**
 * Route Registry
 *
 * Central route definitions with lazy loading for code splitting.
 * Includes route protection for authenticated routes.
 *
 * Route Structure:
 * - Public routes: Login, Signup, Forgot Password, LinkedIn Callback
 * - Protected routes: Dashboard, Business features, Settings, Billing
 *
 * Lazy pages are wrapped in a stable component so wouter's Route never
 * receives React.lazy() directly (avoids "Cannot convert object to primitive value").
 */

import { lazy, ReactNode, Suspense } from 'react';
import { Redirect, Route, Switch } from 'wouter';
import { LoadingSpinner } from '@/components/shell/LoadingSpinner';
import { PageErrorBoundary } from '@/components/shell/ErrorBoundary';
import { AdminRoute, ProtectedRoute, PublicOnlyRoute } from '@/components/guards/ProtectedRoute';
import { BusinessPortalGuard } from '@/components/guards/BusinessPortalGuard';

// Eager imports — auth pages should load instantly
import LoginPage from '@/features/platform/auth/pages/Login';
import SignupPage from '@/features/platform/auth/pages/Signup';

/**
 * Wraps lazy() in a stable function component so Route never receives
 * React.lazy() directly.
 *
 * Each lazy route is wrapped in a PageErrorBoundary so that a render-time
 * crash in one page does not propagate to the top-level boundary and kill
 * the entire app shell.
 */
function lazyRoute(importFn: () => Promise<{ default: React.ComponentType }>) {
  const LazyComponent = lazy(importFn);
  return function LazyRouteWrapper() {
    return (
      <PageErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <LazyComponent />
        </Suspense>
      </PageErrorBoundary>
    );
  };
}

// ═══════════════════════════════════════════════════════════════════════
// LAZY-LOAD PAGES FOR CODE SPLITTING
// ═══════════════════════════════════════════════════════════════════════

// Auth (lazy — not on critical path)
const SsoCallbackPage = lazyRoute(() => import('@/features/platform/auth/pages/SsoCallbackPage'));
const SignUpContinuePage = lazyRoute(
  () => import('@/features/platform/auth/pages/SignUpContinuePage')
);
const ForgotPasswordPage = lazyRoute(
  () => import('@/features/platform/auth/pages/ForgotPasswordPage')
);
const AcceptInvitePage = lazyRoute(() => import('@/features/platform/auth/pages/AcceptInvitePage'));
const NoBusinessAccessPage = lazyRoute(
  () => import('@/features/platform/auth/pages/NoBusinessAccessPage')
);

// Platform
const NotFound = lazyRoute(() => import('@/core/pages/NotFoundPage'));
const OnboardingWizard = lazyRoute(
  () => import('@/features/platform/onboarding/pages/OnboardingWizard')
);
const DashboardHome = lazyRoute(() => import('@/features/platform/dashboard/pages/Home'));
const BillingPage = lazyRoute(() => import('@/features/platform/billing/pages/Billing'));
const SettingsPage = lazyRoute(() => import('@/features/platform/settings/pages/Settings'));
const AboutPage = lazyRoute(() => import('@/features/platform/about/pages/AboutPage'));
const TermsPrivacyPage = lazyRoute(
  () => import('@/features/platform/legal/pages/TermsPrivacyPage')
);

// Business
const ProgramsPage = lazyRoute(() => import('@/features/business/programs/pages/Programs'));
const ProgramDetailPage = lazyRoute(
  () => import('@/features/business/programs/pages/ProgramDetail')
);
const CohortsPage = lazyRoute(() => import('@/features/business/cohorts/pages/Cohorts'));
const CohortDetailPage = lazyRoute(() => import('@/features/business/cohorts/pages/CohortDetail'));
const PipelinePage = lazyRoute(() => import('@/features/business/pipeline/pages/Pipeline'));
const VettingPage = lazyRoute(() => import('@/features/business/vetting/pages/Vetting'));
const SponsorReturnPage = lazyRoute(
  () => import('@/features/business/sponsorship/pages/SponsorReturnPage')
);
const MobilityPage = lazyRoute(() => import('@/features/business/mobility/pages/Mobility'));
const AdaptationPage = lazyRoute(() => import('@/features/business/adaptation/pages/Adaptation'));
const CarePage = lazyRoute(() => import('@/features/business/care/pages/Care'));
const EvidencePage = lazyRoute(() => import('@/features/business/evidence/pages/Evidence'));
const PoolPage = lazyRoute(() => import('@/features/business/pool/pages/Pool'));
const AffiliatesPage = lazyRoute(() => import('@/features/business/affiliates/pages/Affiliates'));

// Secondary
const MessagesPage = lazyRoute(() => import('@/features/secondary/messages/pages/Messages'));
const NotificationsPage = lazyRoute(
  () => import('@/features/secondary/notifications/pages/Notifications')
);

// ═══════════════════════════════════════════════════════════════════════
// ROUTE WRAPPERS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Wrapper for authenticated routes that need auth protection.
 *
 * Two-layer guard:
 *   1. `ProtectedRoute` — user must be signed in (else → /login)
 *   2. `BusinessPortalGuard` — identity must have business tenant
 *      membership (else → /no-access)
 *
 * Layer 2 is what catches cross-portal sign-ins: a citizen identity
 * from journey-web opening business-web passes #1 but not #2.
 */
function AuthenticatedRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requireAuth>
      <BusinessPortalGuard>{children}</BusinessPortalGuard>
    </ProtectedRoute>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ROUTER
// ═══════════════════════════════════════════════════════════════════════

export default function Router() {
  return (
    <Switch>
      {/* ── Public Routes ─────────────────────────────────────────── */}

      {/* Root redirect: authenticated → dashboard, unauthenticated → login */}
      <Route path="/">
        <PublicOnlyRoute redirectTo="/dashboard">
          <LoginPage />
        </PublicOnlyRoute>
      </Route>

      <Route path="/login">
        <PublicOnlyRoute>
          <LoginPage />
        </PublicOnlyRoute>
      </Route>

      <Route path="/signup">
        <PublicOnlyRoute>
          <SignupPage />
        </PublicOnlyRoute>
      </Route>

      {/* A social sign-up Clerk could not finish alone (SIGN_UP_CONTINUE_PATH).
          Not public-only: it ends signed in, on its way out. */}
      <Route path="/signup/continue">
        <SignUpContinuePage />
      </Route>

      <Route path="/forgot-password">
        <ForgotPasswordPage />
      </Route>

      <Route path="/accept-invite">
        <AcceptInvitePage />
      </Route>

      {/*
       * Auth required but NOT wrapped in BusinessPortalGuard —
       * this is the fallback destination when the guard denies access.
       * Wrapping it would create an infinite redirect loop.
       */}
      <Route path="/no-access">
        <ProtectedRoute requireAuth>
          <NoBusinessAccessPage />
        </ProtectedRoute>
      </Route>

      {/* Every social sign-in comes back here (SSO_CALLBACK_PATH, @tonnie/sdk). */}
      <Route path="/auth/sso-callback">
        <SsoCallbackPage />
      </Route>

      {/* ── Onboarding — Auth Required, No Layout ─────────────────── */}

      <Route path="/onboarding">
        <AuthenticatedRoute>
          <OnboardingWizard />
        </AuthenticatedRoute>
      </Route>

      {/* ── Dashboard Routes — Auth Required ──────────────────────── */}

      <Route path="/dashboard">
        <AuthenticatedRoute>
          <DashboardHome />
        </AuthenticatedRoute>
      </Route>

      {/* ── Programs & Cohorts — Auth Required ────────────────────── */}

      <Route path="/programs">
        <AuthenticatedRoute>
          <ProgramsPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/programs/:programId">
        <AuthenticatedRoute>
          <ProgramDetailPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/cohorts">
        <AuthenticatedRoute>
          <CohortsPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/cohorts/:cohortId">
        <AuthenticatedRoute>
          <CohortDetailPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/pipeline">
        <AuthenticatedRoute>
          <PipelinePage />
        </AuthenticatedRoute>
      </Route>
      {/* Pool — Enterprise-only; the page itself shows a non-Enterprise
          tenant the upgrade gate. */}
      <Route path="/pool">
        <AuthenticatedRoute>
          <PoolPage />
        </AuthenticatedRoute>
      </Route>
      {/* Affiliate program (BL-213). The sidebar offers it to an enrolled
          admin; the dashboard endpoints behind it are business_admin-only. */}
      <Route path="/affiliates">
        <AuthenticatedRoute>
          <AffiliatesPage />
        </AuthenticatedRoute>
      </Route>

      {/* ── Main Heroes — Auth Required ───────────────────────────── */}

      <Route path="/vetting">
        <AuthenticatedRoute>
          <VettingPage />
        </AuthenticatedRoute>
      </Route>
      {/* Stripe Checkout return URL for the sponsorship flow.
          Carries `?session_id=cs_…&talentId=…`. The page verifies
          the session, dispatches the activation email (step 3), and
          shows the confirmation card (step 4) before sending the
          admin back to /vetting. */}
      <Route path="/sponsor/return">
        <AuthenticatedRoute>
          <SponsorReturnPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/mobility">
        <AuthenticatedRoute>
          <MobilityPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/adaptation">
        <AuthenticatedRoute>
          <AdaptationPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/care">
        <AuthenticatedRoute>
          <CarePage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/evidence">
        <AuthenticatedRoute>
          <EvidencePage />
        </AuthenticatedRoute>
      </Route>
      {/* Activity moved into the Evidence page as a tab. Keep the old
          path working by redirecting any bookmarked /activity link to
          the Evidence Activity tab. */}
      <Route path="/activity">
        <Redirect to="/evidence?tab=activity" replace />
      </Route>

      {/* ── Secondary Navigation — Auth Required ──────────────────── */}

      <Route path="/messages">
        <AdminRoute>
          <MessagesPage />
        </AdminRoute>
      </Route>
      <Route path="/billing">
        <AdminRoute>
          <BillingPage />
        </AdminRoute>
      </Route>
      <Route path="/settings">
        <AuthenticatedRoute>
          <SettingsPage />
        </AuthenticatedRoute>
      </Route>
      <Route path="/notifications">
        <AuthenticatedRoute>
          <NotificationsPage />
        </AuthenticatedRoute>
      </Route>

      {/* ── Legal & Info Routes (public) ──────────────────────────── */}

      <Route path="/about" component={AboutPage} />
      <Route path="/terms-privacy" component={TermsPrivacyPage} />

      {/* ── 404 Fallback ──────────────────────────────────────────── */}

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}
